/**
 * Help Centre API client (/api/help/). Every payload is already filtered for the
 * signed-in user on the server; this module only caches it for the session.
 *
 * The cache is per Host + user, so switching workspace or account never shows
 * another person's guides. The browser revalidates with ETags underneath.
 */
import api from '@/utils/api'

const cache = new Map()

function ownerKey(user) {
  const host = typeof window !== 'undefined' ? window.location.host : ''
  return `${host}|${user?.id ?? 'anon'}|${user?.user_type?.code || ''}`
}

async function cached(user, name, loader) {
  const key = `${ownerKey(user)}|${name}`
  if (!cache.has(key)) {
    const promise = loader().catch((error) => {
      cache.delete(key)
      throw error
    })
    cache.set(key, promise)
  }
  return cache.get(key)
}

export function clearHelpCache() {
  cache.clear()
}

/** Drop the cached home payload (path progress lives there) after a step changes. */
export function invalidateHelpHome() {
  for (const key of cache.keys()) {
    if (key.endsWith('|home')) cache.delete(key)
  }
}

export async function fetchHelpPath(key) {
  return (await api.get(`/help/paths/${encodeURIComponent(key)}/`)).data
}

/** Download a guide (`articles/<slug>`) or a learning path (`paths/<key>`) as a PDF file. */
export async function downloadHelpPdf(kind, id, fallbackName) {
  const base = kind === 'path' ? `/help/paths/${encodeURIComponent(id)}` : `/help/articles/${String(id).replace(/^\/+|\/+$/g, '')}`
  const response = await api.get(`${base}/pdf/`, { responseType: 'blob' })
  const disposition = response.headers?.get?.('content-disposition') || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = match ? match[1] : fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function setHelpStepDone(pathKey, stepKey, done) {
  const url = `/help/paths/${encodeURIComponent(pathKey)}/steps/${encodeURIComponent(stepKey)}/done/`
  const response = done ? await api.post(url, {}) : await api.delete(url)
  invalidateHelpHome()
  return response.data
}

export function fetchHelpHome(user) {
  return cached(user, 'home', async () => (await api.get('/help/')).data)
}

export function fetchHelpSearchIndex(user) {
  return cached(user, 'search', async () => (await api.get('/help/search-index/')).data)
}

export function fetchHelpContextIndex(user) {
  return cached(user, 'context', async () => (await api.get('/help/context-index/')).data)
}

export async function fetchHelpArticle(slug) {
  const clean = String(slug || '').replace(/^\/+|\/+$/g, '')
  return (await api.get(`/help/articles/${clean}/`)).data
}

export async function fetchPermissionDescription(key) {
  return (await api.get(`/help/permissions/${encodeURIComponent(key)}/`)).data
}

/**
 * Guides for the page the user is on. Tries `pattern?tab=x` first, then the bare
 * route pattern (e.g. `/user/my-business/:id`), and returns unique articles.
 */
export function articlesForRoute(contextIndex, route) {
  const routes = contextIndex?.routes || {}
  const matched = route?.matched?.length ? route.matched[route.matched.length - 1].path : route?.path || ''
  const tab = route?.query?.tab ? String(route.query.tab) : ''
  const keys = tab ? [`${matched}?tab=${tab}`, matched] : [matched]
  const seen = new Set()
  const out = []
  for (const key of keys) {
    for (const article of routes[key] || []) {
      if (seen.has(article.slug)) continue
      seen.add(article.slug)
      out.push(article)
    }
  }
  return out
}
